import moment from 'moment-timezone'
import APP_TIMEZONE from '../../../config/timezone'

// Hardcoded CST timezone
export const userTimezone = APP_TIMEZONE

// current month year
export const currentMonthYear = moment().format('MMMM YYYY')

/**
 * Returns today's date as 'YYYY-MM-DD'
 */
export const getTodayDateTime = moment().format('YYYY-MM-DD HH:mm:ss')

/**
 * Returns the date N days ago as 'YYYY-MM-DD'
 */
export function getDateNDaysAgo(days: number = 14): string {
  return moment().subtract(days, 'days').format('YYYY-MM-DD')
}

export function getDaysLeft(date: any): number {
  const targetDate = moment(date)

  // Today's date
  const today = moment()
  const daysRemaining = targetDate.diff(today, 'days')

  return daysRemaining
}

export const formatToUSDate = (date: any) => {
  return moment(date).format('MM/DD/YYYY') // MM/DD/YYYY US format
}

export const formatToUSDateTime = (date: any) => {
  return moment(date).format('MM/DD/YYYY HH:mm') // MM/DD/YYYY HH:mm US format
}

export const formatToUSDateTimeShort = (date: any) => {
  return moment(date).format('M/D HH:mm') // MM/DD HH:mm US format
}

export const formatTime = (utcString: string): string => {
  // Display time as-is from backend without any timezone conversion
  // Remove the 'Z' suffix if present to prevent UTC interpretation
  const localDateTime = utcString.replace('Z', '')
  return moment(localDateTime).format('hh:mm A')
}

export const formatToAppTimezone = (date: string, format: string = 'MM/DD/YYYY HH:mm') => {
  if (!date) return '-';
  return moment.utc(date)      // parse UTC timestamp
               .tz(APP_TIMEZONE) // convert to CST timezone
               .format(format);
}

// New function specifically for displaying backend datetime values without timezone conversion
export const formatBackendDateTime = (dateTimeString: string): string => {
  // Force moment to treat this as local time, not UTC
  // Remove the 'Z' suffix if present to prevent UTC interpretation
  const localDateTime = dateTimeString.replace('Z', '')
  return moment(localDateTime).format('MM/DD/YYYY hh:mm A')
}

export const formatBackendDate = (dateTimeString: string): string => {
  // Force moment to treat this as local time, not UTC
  // Remove the 'Z' suffix if present to prevent UTC interpretation
  const localDateTime = dateTimeString.replace('Z', '')
  return moment(localDateTime).format('MM/DD/YYYY')
}

export const formatBackendTime = (dateTimeString: string): string => {
  // Force moment to treat this as local time, not UTC
  // Remove the 'Z' suffix if present to prevent UTC interpretation
  const localDateTime = dateTimeString.replace('Z', '')
  return moment(localDateTime).format('hh:mm A')
}

/**
 * Format UTC datetime strings from database to CST timezone
 * Use this for created_at, updated_at, etc.
 */
export const formatUTCToUserDate = (utcString: string): string => {
  return moment(utcString).format('MM/DD/YYYY')
}

export const formatUTCToUserDateTime = (utcString: string): string => {
  return moment(utcString).format('MM/DD/YYYY HH:mm')
}

export const formatUTCToUserTime = (utcString: string): string => {
  return moment(utcString).format('hh:mm A')
}

/**
 * Format billing cycle dates - treat date string as CST business date
 * Since backend sends exact dates in CST, we just format them directly
 */
export const formatBillingCycleDateToUserTz = (
  dateString: string | moment.Moment | any,
  format: string = 'MM/DD/YYYY'
): string => {
  // Backend sends dates in CST, so we just format them directly
  return moment(dateString).format(format)
}

export const formatUtcDateToUserTimezone = (
  utcDate: string | moment.Moment | any,
  format: string = 'MMM D'
): string => {
  return moment(utcDate).format(format)
}

export const formatBillingCycleDateToUser = (
  dateString: string | moment.Moment | any,
  format: string = 'MMM D'
): string => {
  // Billing cycle dates are days not timestamps, so we don't need to add time component
  return moment(dateString).format(format)
}

/**
 * Legacy function - kept for backward compatibility but prefer formatBillingCycleDateToUser
 */
export const formatBillingCycleDateToUserLegacy = (
  utcDateString: string,
  format: string = 'MMM D'
): string => {
  // Parse as regular date and format
  return moment(utcDateString).format(format)
}

export const formatAlignedBillingCycleDate = (
  dateString: string,
  deviceAssignmentUTC?: string,
  format: string = 'MMM D'
): string => {
  // If no device assignment date provided, use standard formatting
  if (!deviceAssignmentUTC) {
    return formatBillingCycleDateToUser(dateString, format)
  }

  // Check if alignment is needed
  const deviceBusinessDate = moment(deviceAssignmentUTC).format('YYYY-MM-DD')
  const cycleDate = moment(dateString).format('YYYY-MM-DD')

  if (deviceBusinessDate !== cycleDate) {
    // Use device business date instead of cycle date for alignment
    return moment(deviceBusinessDate).format(format)
  }

  return moment(dateString).format(format)
}
